import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';

export default function StarSungCompassPage() {
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0d1f3c 50%, #0a1628 100%)' }}>
      {/* Header */}
      <div className="bg-[#C9A227]/90 text-center py-3">
        <p className="text-sm font-bold tracking-widest text-[#0a1628] uppercase">New from Grad Fawkes</p>
      </div>

      {/* Hero Image */}
      <div className="relative w-full aspect-[16/9] max-h-[60vh] overflow-hidden">
        <img 
          src="/publications/oumuamua/star-compass-1.jpg" 
          alt="Oumuamua and the Star-Sung Compass" 
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 relative z-10">
        <Link to="/publications" className="inline-flex items-center gap-2 text-[#C9A227] hover:text-[#d4af37] mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back to Publications
        </Link>

        <h1 className="text-4xl md:text-6xl font-serif text-[#F3EAD3] tracking-wide text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          OUMUAMUA
        </h1>
        <h2 className="text-2xl md:text-3xl font-serif text-[#C9A227] tracking-widest text-center mb-2 uppercase">
          And the Star-Sung Compass
        </h2>
        <p className="text-xl text-[#C9A227] italic text-center mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
          A Tale of Tuwaqachi
        </p>

        <div className="space-y-6 text-[#B7C4D4] text-lg leading-relaxed">
          <p>
            Before the Games. Before the Maw. Before the blue along the fin—there was a droplet world, two moons 
            that would not agree, and a child who asked if the Bright Whale was real.
          </p>
          <p>
            This is the first tale: flame and rain make a pact, a council learns to share a forest, and a song older than 
            silence looks for a body to live in.
          </p>
        </div>

        <div className="my-12 border-t border-[#C9A227]/30" />

        <div className="text-center mb-12">
          <p className="text-[#C9A227] italic text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            When June returns and the prisms ignite, look to the sky.
          </p>
          <p className="text-[#C9A227] italic text-lg mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            You may hear the song, or awaken the dream.
          </p>
        </div>

        {/* Second Image */}
        <div className="my-12">
          <img 
            src="/publications/oumuamua/star-compass-2.jpg" 
            alt="The Driphtwopke" 
            className="w-full rounded-xl shadow-2xl"
          />
        </div>

        <div className="space-y-6 text-[#B7C4D4] text-lg leading-relaxed">
          <p>
            On a world made of droplets, two moons trade places and never agree. Aujarluk warms the western tide. 
            Sikuaq brings ice and clarity. Between them the Wopke gathers—and a chrome-bright child listens.
          </p>
          <p>
            Elara tells him of the Driphtwopke, the Bright Whale who remembers the Compass not as a tool, but as the 
            way the world moved before the song cracked. Flame and rain made a pact. They did not marry. Life was 
            born in that tension.
          </p>
          <p>
            When June's star rises, light bends through the moons like a prism. The Kamapuas slip through the 
            in-between. A rift begins to wake. And a child who maps feelings as well as stars wonders whether the 
            prophecy is pointing at him.
          </p>
        </div>

        <div className="my-12 border-t border-[#C9A227]/30" />

        <div className="text-center">
          <p className="text-[#C9A227] italic text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            The prophecy is not a rule. It is a rhythm.
          </p>
          <p className="text-[#C9A227] italic text-lg mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            The stars have their proper time.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-[#C9A227]/20 text-center">
          <p className="text-[#C9A227] italic mb-4">No ending here. Only the first current.</p>
          <p className="text-[#F3EAD3] font-semibold">By Grad Fawkes</p>
          
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a 
              href="https://a.co/d/07SUOsPj" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF9900] text-[#0a1628] font-semibold rounded-lg hover:bg-[#FFa500] transition-colors"
              data-testid="amazon-buy-btn"
            >
              Buy on Amazon
              <ExternalLink className="w-4 h-4" />
            </a>
            <a 
              href="https://www.facebook.com/profile.php?id=61583179743030" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A227] text-[#0a1628] font-semibold rounded-lg hover:bg-[#d4af37] transition-colors"
            >
              Follow on Facebook
              <ExternalLink className="w-4 h-4" />
            </a>
            <Link 
              to="/publications/blue-flame"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#C9A227] text-[#C9A227] font-semibold rounded-lg hover:bg-[#C9A227]/10 transition-colors"
            >
              Read Book 2: The Blue Flame
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
